import { GOOGLE_MAP_ID_LIGHT_2D } from '@env';
import { merge } from 'lodash';
import { NEVER } from 'rxjs';

import { Injectable } from '@angular/core';

import { ISnListQueryParameters } from '@lib/api';
import { SnLocation } from '@lib/api/entities';
import { SnFloorsAPI, SnLocationsAPI } from '@lib/api/services';
import { SnFormFieldConfig } from '@lib/form';
import { ISnAutocompleteFormFieldConfig } from '@lib/form/autocomplete';
import { ISnFileUploadFormFieldConfigInterface } from '@lib/form/file-upload';
import { ISnGeometryFormFieldConfig } from '@lib/form/geometry';
import { ISnHttpSelectFormFieldConfig } from '@lib/form/http-select';
import { ISnInputFormFieldConfig } from '@lib/form/input';
import { ISnMobilePhoneFormFieldConfig } from '@lib/form/mobile-phone';
import { ISnPlacesAutocompleteFormFieldConfig } from '@lib/form/places-autocomplete';
import { ISnSliderFormFieldConfig } from '@lib/form/slider';
import { SnGoogleGeocoderResult } from '@lib/geo/google/geocoder';
import { GoogleMapStyle } from '@lib/geo/google/map';
import { t } from '@lib/translation';

@Injectable({ providedIn: 'root' })
export class SnLocationFieldsGeneratorService {
  constructor(
    private readonly _locationsAPI: SnLocationsAPI,
    private readonly _floorsAPI: SnFloorsAPI,
  ) {}

  public getLocationSelectField(
    fieldConfig?: Partial<ISnHttpSelectFormFieldConfig<SnLocation>>,
    setParams?: ISnListQueryParameters,
  ): ISnHttpSelectFormFieldConfig<SnLocation> {
    const baseFieldConfig: ISnHttpSelectFormFieldConfig<SnLocation> = {
      key: 'location',
      type: 'http-select',
      templateOptions: {
        label: t('common.location'),
        floatLabel: 'always',
        placeholder: t('common.none'),
        infinityScroll: true,
        valueProp: 'id',
        labelProp: 'name',
        search: true,
        fetchDataFn: (params: ISnListQueryParameters) =>
          this._locationsAPI.getLocationList({ ...params, ...setParams }),
      },
    };

    return merge(baseFieldConfig, fieldConfig);
  }

  public getNameField(fieldConfig?: Partial<ISnInputFormFieldConfig>): ISnInputFormFieldConfig {
    const baseFieldConfig: ISnInputFormFieldConfig = {
      key: 'name',
      type: 'input',
      templateOptions: {
        label: t('common.name'),
        placeholder: t('common.enterName'),
      },
    };

    return merge(baseFieldConfig, fieldConfig);
  }

  public getRedbookField(fieldConfig?: Partial<ISnInputFormFieldConfig>): ISnInputFormFieldConfig {
    const baseFieldConfig: ISnInputFormFieldConfig = {
      key: 'additionalData.redbookId',
      type: 'input',
      templateOptions: {
        label: t('locations.fields.redbookId'),
        placeholder: t('locations.placeholders.enterRedbookId'),
      },
    };

    return merge(baseFieldConfig, fieldConfig);
  }

  public getContactNameField(
    fieldConfig?: Partial<ISnInputFormFieldConfig>,
  ): ISnInputFormFieldConfig {
    const baseFieldConfig: ISnInputFormFieldConfig = {
      key: 'contactName',
      type: 'input',
      templateOptions: {
        label: t('common.name'),
        placeholder: t('common.enterName'),
      },
    };

    return merge(baseFieldConfig, fieldConfig);
  }

  public getContactEmailField(
    fieldConfig?: Partial<ISnInputFormFieldConfig>,
  ): ISnInputFormFieldConfig {
    const baseFieldConfig: ISnInputFormFieldConfig = {
      key: 'contactEmail',
      type: 'input',
      validators: {
        validation: ['email'],
      },
      templateOptions: {
        label: t('common.email'),
        placeholder: t('common.enterEmail'),
      },
    };

    return merge(baseFieldConfig, fieldConfig);
  }

  public getContactMobilePhoneField(
    fieldConfig?: Partial<ISnMobilePhoneFormFieldConfig>,
  ): ISnMobilePhoneFormFieldConfig {
    const baseFieldConfig: ISnMobilePhoneFormFieldConfig = {
      key: 'contactPhone',
      type: 'mobile-phone',
      templateOptions: {
        label: t('common.phone'),
      },
    };

    return merge(baseFieldConfig, fieldConfig);
  }

  public getGeometryField(
    fieldConfig?: Partial<ISnGeometryFormFieldConfig>,
  ): ISnGeometryFormFieldConfig {
    const baseFieldConfig: ISnGeometryFormFieldConfig = {
      key: 'geometry',
      type: 'geometry',
      templateOptions: {
        mapOptions: {
          zoom: 17,
          style: GoogleMapStyle.Satellite,
          mapId: GOOGLE_MAP_ID_LIGHT_2D,
        },
      },
    };

    return merge(baseFieldConfig, fieldConfig);
  }

  public getDefaultZoomField(
    fieldConfig?: Partial<ISnSliderFormFieldConfig>,
  ): ISnSliderFormFieldConfig {
    const baseFieldConfig: ISnSliderFormFieldConfig = {
      key: 'defaultZoom',
      type: 'slider',
      templateOptions: {
        label: t('locations.fields.defaultZoom'),
        min: 10,
        max: 22,
        value: 20,
        step: 1,
        thumbLabel: true,
      },
    };

    return merge(baseFieldConfig, fieldConfig);
  }

  public getAddressField(
    fieldConfig?: Partial<ISnAutocompleteFormFieldConfig<SnGoogleGeocoderResult>>,
  ): ISnAutocompleteFormFieldConfig<SnGoogleGeocoderResult> {
    const baseFieldConfig: ISnPlacesAutocompleteFormFieldConfig = {
      key: 'address',
      type: 'places-autocomplete',
      templateOptions: {
        label: t('common.address'),
        placeholder: t('common.enterAddress'),
      },
    };

    return merge(baseFieldConfig, fieldConfig);
  }

  public getCityField(fieldConfig?: Partial<ISnInputFormFieldConfig>): ISnInputFormFieldConfig {
    const baseFieldConfig: ISnInputFormFieldConfig = {
      key: 'city',
      type: 'input',
      templateOptions: {
        label: t('common.city'),
        placeholder: t('incidentManagement.dialogs.createNewIncident.enterCity'),
      },
    };

    return merge(baseFieldConfig, fieldConfig);
  }

  public getTownField(fieldConfig?: Partial<ISnInputFormFieldConfig>): ISnInputFormFieldConfig {
    const baseFieldConfig: ISnInputFormFieldConfig = {
      key: 'town',
      type: 'input',
      templateOptions: {
        label: t('locations.fields.town'),
        placeholder: t('locations.placeholders.enterTown'),
      },
    };

    return merge(baseFieldConfig, fieldConfig);
  }

  public getCountyField(fieldConfig?: Partial<ISnInputFormFieldConfig>): ISnInputFormFieldConfig {
    const baseFieldConfig: ISnInputFormFieldConfig = {
      key: 'county',
      type: 'input',
      templateOptions: {
        label: t('locations.fields.county'),
        placeholder: t('locations.placeholders.enterCounty'),
      },
    };

    return merge(baseFieldConfig, fieldConfig);
  }

  public getPostCode(fieldConfig?: Partial<ISnInputFormFieldConfig>): ISnInputFormFieldConfig {
    const baseFieldConfig: ISnInputFormFieldConfig = {
      key: 'postcode',
      type: 'input',
      templateOptions: {
        label: t('locations.fields.postcode'),
        placeholder: t('locations.placeholders.enterPostcode'),
      },
    };

    return merge(baseFieldConfig, fieldConfig);
  }

  public getStateField(fieldConfig?: Partial<ISnInputFormFieldConfig>): ISnInputFormFieldConfig {
    const baseFieldConfig: ISnInputFormFieldConfig = {
      key: 'state',
      type: 'input',
      templateOptions: {
        label: t('common.state'),
        placeholder: t('incidentManagement.dialogs.createNewIncident.enterState'),
      },
    };

    return merge(baseFieldConfig, fieldConfig);
  }

  public getZipField(fieldConfig?: Partial<ISnInputFormFieldConfig>): ISnInputFormFieldConfig {
    const baseFieldConfig: ISnInputFormFieldConfig = {
      key: 'zip',
      type: 'input',
      templateOptions: {
        label: t('vault.incidents.fieldsGenerator.zip'),
        placeholder: t('locations.placeholders.enterZip'),
      },
    };

    return merge(baseFieldConfig, fieldConfig);
  }
  public getDefaultFloorField(
    fieldConfig?: Partial<ISnInputFormFieldConfig>,
  ): ISnHttpSelectFormFieldConfig {
    const baseFieldConfig: ISnHttpSelectFormFieldConfig = {
      key: 'defaultFloor',
      type: 'http-select',
      templateOptions: {
        label: t('locations.fields.defaultFloor'),
        infinityScroll: true,
        valueProp: 'id',
        labelProp: 'name',
        fetchDataFn: (params: ISnListQueryParameters, model: any, field) => {
          const locationId: string = field.parent.model.id;
          return locationId ? this._floorsAPI.getFloorList(locationId, params) : NEVER;
        },
      },
    };

    return merge(baseFieldConfig, fieldConfig);
  }

  public getLocationTypeSelectField(
    fieldConfig?: Partial<ISnInputFormFieldConfig>,
  ): ISnHttpSelectFormFieldConfig {
    const baseFieldConfig: ISnHttpSelectFormFieldConfig = {
      key: 'type',
      type: 'http-select',
      templateOptions: {
        label: t('common.type'),
        infinityScroll: true,
        valueProp: 'id',
        labelProp: 'name',
        fetchDataFn: (params) => this._locationsAPI.getLocationTypeList(params),
      },
    };

    return merge(baseFieldConfig, fieldConfig);
  }

  public getLocationAddressField(
    fieldConfig?: Partial<ISnAutocompleteFormFieldConfig<SnGoogleGeocoderResult>>,
  ): ISnAutocompleteFormFieldConfig<SnGoogleGeocoderResult> {
    const baseFieldConfig: ISnPlacesAutocompleteFormFieldConfig = {
      key: 'address',
      type: 'places-autocomplete',
      templateOptions: {
        label: t('locations.fields.locationAddress'),
        placeholder: t('locations.placeholders.enterLocationAddress'),
      },
    };

    return merge(baseFieldConfig, fieldConfig);
  }

  public getUKLocationAddressField(
    fieldConfig?: Partial<ISnAutocompleteFormFieldConfig<SnGoogleGeocoderResult>>,
  ): ISnAutocompleteFormFieldConfig<SnGoogleGeocoderResult> {
    const baseFieldConfig: ISnPlacesAutocompleteFormFieldConfig = {
      key: 'address',
      type: 'places-autocomplete',
      templateOptions: {
        label: t('locations.fields.address1'),
        placeholder: t('locations.placeholders.enterAddress1'),
      },
    };

    return merge(baseFieldConfig, fieldConfig);
  }

  public getLocationTypeField(fieldConfig?: Partial<SnFormFieldConfig>): SnFormFieldConfig {
    const baseFieldConfig: SnFormFieldConfig = {
      type: 'radio',
      key: 'locationType',
      templateOptions: {
        label: t('locations.fields.locationType'),
        hideLabel: false,
        options: [
          { label: t('locations.options.residential'), value: 1, details: 'Detail 1' },
          { label: t('locations.options.business'), value: 2, details: 'Detail 2' },
          { label: t('locations.options.other'), value: 3, details: 'Detail 3' },
        ],
      },
    };

    return merge(baseFieldConfig, fieldConfig);
  }

  public getBusinessNameField(
    fieldConfig?: Partial<ISnInputFormFieldConfig>,
  ): ISnInputFormFieldConfig {
    const baseFieldConfig: ISnInputFormFieldConfig = {
      key: 'businessName',
      type: 'input',
      templateOptions: {
        label: t('locations.fields.businessName'),
        placeholder: t('locations.placeholders.enterBusinessName'),
      },
    };

    return merge(baseFieldConfig, fieldConfig);
  }

  public getLocationNameField(
    fieldConfig?: Partial<ISnInputFormFieldConfig>,
  ): ISnInputFormFieldConfig {
    const baseFieldConfig: ISnInputFormFieldConfig = {
      key: 'locationName',
      type: 'input',
      templateOptions: {
        label: t('locations.fields.locationName'),
        placeholder: t('locations.placeholders.enterLocationName'),
      },
    };

    return merge(baseFieldConfig, fieldConfig);
  }

  public getUKLocationNameField(
    fieldConfig?: Partial<ISnInputFormFieldConfig>,
  ): ISnInputFormFieldConfig {
    const baseFieldConfig: ISnInputFormFieldConfig = {
      key: 'address2',
      type: 'input',
      templateOptions: {
        label: t('locations.fields.address2'),
        placeholder: t('locations.placeholders.enterAddress2'),
      },
    };

    return merge(baseFieldConfig, fieldConfig);
  }

  public getFileUploadField(
    fieldConfig?: Partial<ISnFileUploadFormFieldConfigInterface>,
  ): ISnFileUploadFormFieldConfigInterface {
    const baseFieldConfig: ISnFileUploadFormFieldConfigInterface = {
      key: 'files',
      type: 'file-upload',
      templateOptions: {
        multiple: true,
        allowPreview: true,
      },
    };

    return merge(baseFieldConfig, fieldConfig);
  }
  public getInternalCameraNumberField(fieldConfig?: Partial<SnFormFieldConfig>): SnFormFieldConfig {
    const baseFieldConfig: SnFormFieldConfig = {
      type: 'select',
      key: 'internalCamNumber',
      templateOptions: {
        label: t('locations.fields.numberOfInternalCameras'),
        hideLabel: false,
        options: [
          { label: '0', value: 0 },
          { label: '1', value: 1 },
          { label: '2', value: 2 },
          { label: '3', value: 3 },
          { label: '4', value: 4 },
          { label: '5+', value: 5 },
          { label: '16+', value: 16 },
          { label: '32+', value: 32 },
          { label: '64+', value: 64 },
        ],
      },
    };

    return merge(baseFieldConfig, fieldConfig);
  }

  public getExternalCameraNumberField(fieldConfig?: Partial<SnFormFieldConfig>): SnFormFieldConfig {
    const baseFieldConfig: SnFormFieldConfig = {
      type: 'select',
      key: 'externalCamNumber',
      templateOptions: {
        label: t('locations.fields.numberOfExternalCameras'),
        hideLabel: false,
        options: [
          { label: '0', value: 0 },
          { label: '1', value: 1 },
          { label: '2', value: 2 },
          { label: '3', value: 3 },
          { label: '4', value: 4 },
          { label: '5+', value: 5 },
          { label: '16+', value: 16 },
          { label: '32+', value: 32 },
          { label: '64+', value: 64 },
        ],
      },
    };

    return merge(baseFieldConfig, fieldConfig);
  }
}
